// SPDX-License-Identifier: AGPL-3.0-or-later

import {isInsidePioneerchatDesktop} from '@app/features/app/components/layout/pioneer/DesktopAppDetection';
import styles from '@app/features/app/components/layout/pioneer/PioneerSidebar.module.css';
import {
	DESKTOP_APP_DESCRIPTOR,
	DOWNLOAD_FOR_MAC_DESCRIPTOR,
	DOWNLOAD_FOR_WINDOWS_DESCRIPTOR,
	DOWNLOAD_NOT_READY_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import {useLingui} from '@lingui/react/macro';
import {AppleLogoIcon, DownloadSimpleIcon, WindowsLogoIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {useEffect, useId, useRef, useState} from 'react';

const INSTALLERS = [
	{key: 'mac', href: '/download/Pioneerchat-mac.dmg', icon: AppleLogoIcon, label: DOWNLOAD_FOR_MAC_DESCRIPTOR},
	{
		key: 'windows',
		href: '/download/Pioneerchat-windows.exe',
		icon: WindowsLogoIcon,
		label: DOWNLOAD_FOR_WINDOWS_DESCRIPTOR,
	},
] as const;

type Availability = Partial<Record<(typeof INSTALLERS)[number]['key'], boolean>>;

function preferredInstaller(): string {
	const platform = typeof navigator === 'undefined' ? '' : navigator.userAgent;
	return /Windows/i.test(platform) ? 'windows' : 'mac';
}

export function DesktopDownloadMenu({variant = 'row'}: {variant?: 'row' | 'icon'}) {
	const {i18n} = useLingui();
	const [open, setOpen] = useState(false);
	const [available, setAvailable] = useState<Availability>({});
	const [anchor, setAnchor] = useState<{left: number; bottom: number} | null>(null);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const menuId = useId();

	useEffect(() => {
		if (!open) return;
		// The menu sits in a fixed layer so the sidebar's edge can't clip it.
		const rect = rootRef.current?.getBoundingClientRect();
		if (rect) setAnchor({left: Math.max(8, rect.left - 8), bottom: window.innerHeight - rect.top + 8});
		let cancelled = false;
		for (const installer of INSTALLERS) {
			fetch(installer.href, {method: 'HEAD', cache: 'no-store'})
				.then((response) => response.ok)
				.catch(() => false)
				.then((ok) => {
					if (!cancelled) setAvailable((current) => ({...current, [installer.key]: ok}));
				});
		}
		const onPointerDown = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('pointerdown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			cancelled = true;
			document.removeEventListener('pointerdown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [open]);

	if (isInsidePioneerchatDesktop()) return null;

	const preferred = preferredInstaller();
	const ordered = [...INSTALLERS].sort((a, b) => Number(b.key === preferred) - Number(a.key === preferred));

	return (
		<div
			className={variant === 'icon' ? styles.downloadIcon : styles.download}
			ref={rootRef}
			data-flx="app.pioneer.desktop-download-menu.download"
		>
			{variant === 'icon' ? (
				<button
					type="button"
					className={clsx(styles.iconTrigger, open && styles.iconTriggerOn)}
					aria-label={i18n._(DESKTOP_APP_DESCRIPTOR)}
					title={i18n._(DESKTOP_APP_DESCRIPTOR)}
					aria-expanded={open}
					aria-controls={menuId}
					onClick={() => setOpen((value) => !value)}
					data-flx="app.pioneer.desktop-download-menu.icon-trigger.set-open.button"
				>
					<DownloadSimpleIcon
						size={20}
						aria-hidden="true"
						data-flx="app.pioneer.desktop-download-menu.download-simple-icon"
					/>
				</button>
			) : (
				<button
					type="button"
					className={clsx(styles.row, styles.rowQuiet, open && styles.rowOn)}
					aria-expanded={open}
					aria-controls={menuId}
					onClick={() => setOpen((value) => !value)}
					data-flx="app.pioneer.desktop-download-menu.row.set-open.button"
				>
					<span className={styles.lead} data-flx="app.pioneer.desktop-download-menu.lead">
						<DownloadSimpleIcon
							size={16}
							aria-hidden="true"
							data-flx="app.pioneer.desktop-download-menu.download-simple-icon--2"
						/>
					</span>
					{i18n._(DESKTOP_APP_DESCRIPTOR)}
				</button>
			)}
			{open && (
				<div
					className={styles.downloadMenu}
					style={anchor ? {left: anchor.left, bottom: anchor.bottom} : undefined}
					id={menuId}
					data-flx="app.pioneer.desktop-download-menu.download-menu"
				>
					{ordered.map((installer) => {
						const Icon = installer.icon;
						const ready = available[installer.key] !== false;
						return ready ? (
							<a
								key={installer.key}
								className={clsx(styles.downloadItem, installer.key === preferred && styles.downloadItemPrimary)}
								href={installer.href}
								download
								onClick={() => setOpen(false)}
								data-flx="app.pioneer.desktop-download-menu.download-item.set-open"
							>
								<Icon size={18} weight="regular" aria-hidden="true" data-flx="app.pioneer.desktop-download-menu.icon" />
								{i18n._(installer.label)}
							</a>
						) : (
							<span
								key={installer.key}
								className={clsx(styles.downloadItem, styles.downloadItemMissing)}
								data-flx="app.pioneer.desktop-download-menu.download-item"
							>
								<Icon
									size={18}
									weight="regular"
									aria-hidden="true"
									data-flx="app.pioneer.desktop-download-menu.icon--2"
								/>
								<span data-flx="app.pioneer.desktop-download-menu.span">{i18n._(installer.label)}</span>
								<span className={styles.downloadNote} data-flx="app.pioneer.desktop-download-menu.download-note">
									{i18n._(DOWNLOAD_NOT_READY_DESCRIPTOR)}
								</span>
							</span>
						);
					})}
				</div>
			)}
		</div>
	);
}
